import React, { useEffect, useState } from 'react';

import constants from 'constants/constants';
import moment from 'moment-timezone';
import { Control, Controller, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import colorsVars from 'styles/colors.module.scss';
import { monthsDuration } from 'utilitys/helpers/general';

import { useCustomSnackBar } from 'hooks/useCustomSnackBar';

import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';

import { Radio } from 'components/DesignSystem/Radio';
import { CustomizedRoundIcon } from 'components/common';
import { IFields } from 'components/pages/PhasePerformanceManageMapProjectPage/components/MapProject/MapProject';
import classes from 'components/pages/PhasePerformanceManageMapProjectPage/components/MapProject/components/SingleGate/SingleGate.module.scss';
import {
    formatEffectiveDateLabel,
    getEffectiveDate,
} from 'components/pages/PhasePerformanceManageMapProjectPage/components/MapProject/components/SingleGate/SingleGate.utils';
import { ActivitiesSelect } from 'components/pages/PhasePerformanceManageMapProjectPage/components/MapProject/components/SingleGate/components/ActivitiesSelect/ActivitiesSelect';
import {
    IMappingPhaseForSelector,
    IMilestoneMapper,
    ISingleGateWithPhase,
} from 'components/pages/PhasePerformanceManagePage/components/Manage/Manage.types';

const { textColor } = colorsVars;
interface ISingleGateProps {
    item: ISingleGateWithPhase;
    isLastPhase: boolean;
    control: Control<IFields>;
    index: number;
    setValue: UseFormSetValue<IFields>;
    isError: boolean;
    watch: UseFormWatch<IFields>;
    contractId: number | undefined;
    phase?: IMappingPhaseForSelector;
}

export const SingleGate = ({
    item,
    isLastPhase,
    control,
    index,
    setValue,
    isError,
    watch,
    contractId,
    phase,
}: ISingleGateProps) => {
    const { handleSnackBar } = useCustomSnackBar();
    const currentStartMilestoneDate = watch(`phases.${index}.start_milestone`);
    const currentFinishMilestoneDate = watch(`phases.${index}.finish_milestone`);
    const startMilestoneIsStartDate = watch(`phases.${index}.start_milestone_is_start_date`);
    const finishMilestoneIsStartDate = watch(`phases.${index}.finish_milestone_is_start_date`);

    const [isEditMode, setIsEditMode] = useState<boolean>(Boolean(phase));

    useEffect(() => {
        if (phase) {
            handleStartMilestone(phase.start_milestone ?? null);
            handleEndMilestone(phase.finish_milestone ?? null);
            setIsEditMode(false);
        }
    }, []);

    const handleStartMilestone = (value: IMilestoneMapper | null) => {
        if (!value) {
            setValue(`phases.${index}.start_milestone`, null, { shouldDirty: true });
            setValue(`phases.${index}.finish_milestone`, null, { shouldDirty: true });
            return;
        }

        const dateLabel = formatEffectiveDateLabel(value, startMilestoneIsStartDate ?? false);

        setValue(`phases.${index}.start_milestone`, { ...value, dateLabel }, { shouldDirty: !isEditMode });
        setValue(`phases.${index}.finish_milestone`, null, { shouldDirty: !isEditMode });
    };

    const handleEndMilestone = (value: IMilestoneMapper | null) => {
        if (isEditMode) {
            const prevValue = watch(`phases.${index - 1}.finish_milestone`);
            if (prevValue) {
                const prevDateLabel = formatEffectiveDateLabel(prevValue, startMilestoneIsStartDate ?? false);
                setValue(`phases.${index}.start_milestone`, { ...prevValue, dateLabel: prevDateLabel });
            }
        }

        if (!value) {
            setValue(`phases.${index}.finish_milestone`, null, { shouldDirty: true });
            !isLastPhase && setValue(`phases.${index + 1}.start_milestone`, null, { shouldDirty: true });
            !isLastPhase && setValue(`phases.${index + 1}.finish_milestone`, null, { shouldDirty: true });
            return;
        }

        const effectiveFinishDate = getEffectiveDate(value, finishMilestoneIsStartDate ?? false);
        const effectiveStartDate = currentStartMilestoneDate
            ? getEffectiveDate(currentStartMilestoneDate, startMilestoneIsStartDate ?? false)
            : phase?.start_milestone
              ? getEffectiveDate(phase.start_milestone, startMilestoneIsStartDate ?? false)
              : undefined;

        if (effectiveStartDate !== undefined && effectiveFinishDate < effectiveStartDate) {
            handleSnackBar(`Finish Gate date should be greater than start gate date`, 'error');
            return;
        }

        const dateLabel = moment(effectiveFinishDate).format(constants.formats.date.default);

        setValue(`phases.${index}.finish_milestone`, { ...value, dateLabel }, { shouldDirty: !isEditMode });

        !isLastPhase &&
            setValue(`phases.${index + 1}.start_milestone`, { ...value, dateLabel }, { shouldDirty: !isEditMode });
        !isLastPhase && setValue(`phases.${index + 1}.finish_milestone`, null, { shouldDirty: !isEditMode });
    };

    const phaseDuration = (() => {
        if (!currentStartMilestoneDate || !currentFinishMilestoneDate) return '_';
        const effectiveStartDate = getEffectiveDate(currentStartMilestoneDate, startMilestoneIsStartDate ?? false);
        const effectiveFinishDate = getEffectiveDate(currentFinishMilestoneDate, finishMilestoneIsStartDate ?? false);
        return `${monthsDuration(effectiveFinishDate, effectiveStartDate)} month`;
    })();

    return (
        <div className={classes.singleGate}>
            <div className={classes.number}>
                <CustomizedRoundIcon hoverColor={'none'}>{item.sequence + 1}</CustomizedRoundIcon>
                <div className={`${classes.lineBetweenNumbers} ${isLastPhase ? classes.lastPhase : ''}`} />
            </div>
            <div className={`${classes.content} ${isError ? classes.error : ''}`}>
                <div className={classes.leftSide}>
                    <div className={classes.title}>{item.name}</div>
                    <div className={classes.gatesWrapper}>
                        <div className={classes.gate}>
                            <div className={classes.gateTitle}>{item.start_gate}</div>
                            <div data-testid={'gate-select-wrapper'} className={classes.gateSelectWrapper}>
                                <ActivitiesSelect
                                    name={`phases.${index}.start_milestone`}
                                    control={control}
                                    rules={{ required: 'Start activity is required' }}
                                    contractId={contractId}
                                    isClearable={true}
                                    onChange={handleStartMilestone}
                                    isDisabled={item.sequence !== 0}
                                    placeholder={'Select activity...'}
                                />
                            </div>
                            <Controller
                                render={({ field }) => (
                                    <div className={classes.dateTypeRadioGroup}>
                                        <Radio
                                            label={'Start Date'}
                                            name={`phases.${index}.start_milestone_is_start_date`}
                                            checked={field.value === true}
                                            onChange={() => field.onChange(true)}
                                        />
                                        <Radio
                                            label={'Finish Date'}
                                            name={`phases.${index}.start_milestone_is_start_date`}
                                            checked={field.value !== true}
                                            onChange={() => field.onChange(false)}
                                        />
                                    </div>
                                )}
                                name={`phases.${index}.start_milestone_is_start_date`}
                                control={control}
                            />
                            <div className={classes.phaseDate}>
                                <div className={classes.phaseDateTitle}>Phase Start</div>
                                <div className={classes.date}>
                                    {currentStartMilestoneDate
                                        ? formatEffectiveDateLabel(
                                              currentStartMilestoneDate,
                                              startMilestoneIsStartDate ?? false,
                                          )
                                        : '_'}
                                </div>
                            </div>
                        </div>

                        <div data-testid={'arrow-icon'} className={classes.arrowIconWrapper}>
                            <MuiIcon icon={'arrow_forward'} fontSize={'2.2rem'} color={textColor} />
                        </div>

                        <div className={classes.gate}>
                            <div className={classes.gateTitle}>{item.end_gate}</div>
                            <div data-testid={'gate-select-wrapper'} className={classes.gateSelectWrapper}>
                                <ActivitiesSelect
                                    name={`phases.${index}.finish_milestone`}
                                    control={control}
                                    rules={{ required: 'Finish activity is required' }}
                                    contractId={contractId}
                                    isClearable={true}
                                    onChange={handleEndMilestone}
                                    isDisabled={!currentStartMilestoneDate}
                                    placeholder={'Select activity...'}
                                />
                            </div>
                            <Controller
                                render={({ field }) => (
                                    <div className={classes.dateTypeRadioGroup}>
                                        <Radio
                                            label={'Start Date'}
                                            name={`phases.${index}.finish_milestone_is_start_date`}
                                            checked={field.value === true}
                                            onChange={() => field.onChange(true)}
                                        />
                                        <Radio
                                            label={'Finish Date'}
                                            name={`phases.${index}.finish_milestone_is_start_date`}
                                            checked={field.value !== true}
                                            onChange={() => field.onChange(false)}
                                        />
                                    </div>
                                )}
                                name={`phases.${index}.finish_milestone_is_start_date`}
                                control={control}
                            />
                            <div className={classes.phaseDate}>
                                <div className={classes.phaseDateTitle}>Phase End</div>
                                <div className={classes.date}>
                                    {currentFinishMilestoneDate
                                        ? formatEffectiveDateLabel(
                                              currentFinishMilestoneDate,
                                              finishMilestoneIsStartDate ?? false,
                                          )
                                        : '_'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div data-testid={'duration'} className={classes.duration}>
                    <div>
                        <div data-testid={'duration-title'} className={classes.durationTitle}>
                            Phase Duration
                        </div>
                        <div data-testid={'duration-date'} className={classes.durationDate}>
                            {phaseDuration}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
